'use client';

import { createClient } from '@/lib/supabase/client';

export interface JobListing {
  id: string;
  title: string;
  company: string;
  location: string;
  region: string;
  licenseRequired: string;
  pdpRequired: boolean;
  jobType: string;
  salaryDisplay: string;
  salaryMin: number;
  postedDate: string;
  urgency: 'high' | 'medium' | 'low';
  openings: number;
  isActive: boolean;
}

export interface JobApplication {
  id: string;
  userId: string;
  jobListingId: string | null;
  jobTitle: string;
  company: string;
  location: string;
  region: string;
  salary: string;
  appliedDate: string;
  status: 'auto-applied' | 'under-review' | 'shortlisted' | 'interview' | 'offer' | 'rejected';
  response: string;
  autoApplied: boolean;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

function mapJobListing(row: any): JobListing {
  return {
    id: row.id,
    title: row.title,
    company: row.company,
    location: row.location,
    region: row.region,
    licenseRequired: row.license_required,
    pdpRequired: row.pdp_required,
    jobType: row.job_type,
    salaryDisplay: row.salary_display,
    salaryMin: row.salary_min,
    postedDate: row.posted_date
      ? new Date(row.posted_date).toLocaleDateString('en-GB').replace(/\//g, '/')
      : '',
    urgency: row.urgency,
    openings: row.openings,
    isActive: row.is_active,
  };
}

function mapJobApplication(row: any): JobApplication {
  return {
    id: row.id,
    userId: row.user_id,
    jobListingId: row.job_listing_id,
    jobTitle: row.job_title,
    company: row.company,
    location: row.location,
    region: row.region,
    salary: row.salary ?? '',
    appliedDate: row.applied_date
      ? new Date(row.applied_date).toLocaleDateString('en-GB').replace(/\//g, '/')
      : '',
    status: row.status,
    response: row.response ?? '',
    autoApplied: row.auto_applied,
  };
}

export const jobService = {
  async getJobListings(): Promise<JobListing[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('job_listings')
        .select('*')
        .eq('is_active', true)
        .order('posted_date', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Job listings fetch error:', error.message);
        return [];
      }
      return data?.map(mapJobListing) ?? [];
    } catch (err: any) {
      console.log('Schema-related error:', err.message);
      throw err;
    }
  },

  async getUserApplications(userId: string): Promise<JobApplication[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .select('*')
        .eq('user_id', userId)
        .order('applied_date', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Applications fetch error:', error.message);
        return [];
      }
      return data?.map(mapJobApplication) ?? [];
    } catch (err: any) {
      console.log('Schema-related error:', err.message);
      throw err;
    }
  },

  async getApplicationStats(userId: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .select('status')
        .eq('user_id', userId);

      if (error) {
        if (isSchemaError(error)) throw error;
        return { total: 0, underReview: 0, responses: 0, shortlisted: 0 };
      }

      const total = data?.length ?? 0;
      const underReview = data?.filter((a) => a.status === 'under-review').length ?? 0;
      const responses = data?.filter((a) =>
        ['shortlisted', 'interview', 'offer', 'rejected'].includes(a.status)
      ).length ?? 0;
      const shortlisted = data?.filter((a) =>
        ['shortlisted', 'interview', 'offer'].includes(a.status)
      ).length ?? 0;

      return { total, underReview, responses, shortlisted };
    } catch (err: any) {
      console.log('Schema-related error:', err.message);
      return { total: 0, underReview: 0, responses: 0, shortlisted: 0 };
    }
  },

  async autoApplyToJob(
    userId: string,
    job: JobListing
  ): Promise<JobApplication | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .insert({
          user_id: userId,
          job_listing_id: job.id,
          job_title: job.title,
          company: job.company,
          location: job.location,
          region: job.region,
          salary: job.salaryDisplay,
          status: 'auto-applied',
          response: 'Application submitted — awaiting review',
          auto_applied: true,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Auto-apply error:', error.message);
        return null;
      }
      return mapJobApplication(data);
    } catch (err: any) {
      console.log('Schema-related error:', err.message);
      throw err;
    }
  },
};
